import { axiosCancellationRequests } from '../../../config/axios-setup'
import type { NotificationService } from '../../../services'

/** A notification a condition can match, resolved to what the reader recognises it by. */
export interface NotificationOption {
    /** The id the condition stores. A string, the way the service states it. */
    id: string
    /** The notification's title, shown alongside its id. */
    title: string
}

export interface NotificationSource {
    /**
     * The notifications matching a term, or the empty list where the read was
     * abandoned or nothing matched. A read that sees its signal raised must not
     * resolve with results.
     */
    search(term: string, signal: AbortSignal): Promise<NotificationOption[]>
}

/**
 * The notification kinds a clicked condition draws on.
 *
 * These are an allowlist the engine agrees on, not a default to be widened: a
 * condition that matched a kind not listed here would target an audience the engine
 * cannot reproduce, so the set is stated once and passed verbatim.
 */
const NOTIFICATION_SOURCES = 'MANUAL,FEED,CAMPAIGN'
const NOTIFICATION_STATUSES = 'ACTIVE,COMPLETED,SCHEDULED,QUEUED,DELIVERING'

/**
 * What a notification search's cancellation key is built from.
 *
 * The transport cancels a request by name, and a name held by more than one request
 * cancels whichever one happens to hold it at that moment — so an abandoned search's
 * stop would drop whatever is in flight instead, and the reader would be told their
 * notification does not exist. Every read therefore takes a key of its own, which it
 * alone can cancel, and drops it once the read is over so the registry does not gain
 * an entry per keystroke.
 */
const CANCELLATION_KEY_PREFIX = 'sqb.searchNotifications'

/** Distinguishes one read's key from every other's, including across sources. */
let reads = 0

/** The part of the notification service this needs, named so a caller can stand it in. */
export type NotificationReader = Pick<NotificationService, 'fetchNotificationsByDomainId'>

/**
 * Where a domain's notifications are searched.
 *
 * Only a domain owns notifications, so a source exists only at domain level and the
 * renderer above it searches nothing where none is supplied.
 */
export function createNotificationSource(domainId: number, reader: NotificationReader): NotificationSource {
    return {
        async search(term, signal) {
            // A signal already raised has nothing to start: no request exists yet, so
            // there is none to release either.
            if (signal.aborted) return []

            const cancellationKey = `${CANCELLATION_KEY_PREFIX}.${(reads += 1)}`

            // The service cancels by name rather than by signal, so the signal is
            // adapted onto the cancellation it does understand. The key is this read's
            // alone, so this can only ever release this read's own request.
            const stop = () => {
                if (cancellationKey in axiosCancellationRequests) axiosCancellationRequests[cancellationKey]()
            }

            signal.addEventListener('abort', stop, { once: true })

            try {
                const { notifications, cancelled, error } = await reader.fetchNotificationsByDomainId(
                    domainId,
                    {
                        search: term,
                        pagination: 0,
                        source: NOTIFICATION_SOURCES,
                        status: NOTIFICATION_STATUSES,
                    },
                    false,
                    cancellationKey,
                )

                if (cancelled || signal.aborted) return []

                // A read that failed must not resolve as a read that matched nothing.
                // The two are indistinguishable once both are an empty list, and the
                // wrong one of them sends the reader hunting for something that is there.
                if (error) {
                    throw error instanceof Error
                        ? error
                        : new Error(`The notification search could not be read: ${String(error)}`)
                }

                return notifications
                    .map((notification) => ({
                        id: notification.id,
                        title: notification.template?.channels?.default?.title ?? '',
                    }))
                    .filter((option): option is NotificationOption => typeof option.id === 'string' && option.id !== '')
            } finally {
                signal.removeEventListener('abort', stop)
                // The transport drops the key of a request it answered, but not of one it
                // dropped — and a key per read would otherwise accumulate one per keystroke.
                delete axiosCancellationRequests[cancellationKey]
            }
        },
    }
}
